import { useState, useCallback, useMemo } from 'react'
import {
  preflightMakeAppLive,
  triggerProductionBuild,
  updateApp,
  type App,
  type SourcePreflightResponse,
} from '../lib/api'
import { PublishAppModal } from '../components/PublishAppModal'

interface UsePublishAppOptions {
  // Called after triggerProductionBuild succeeds — caller can refresh list/UI.
  onPublishStarted?: (app: App) => void | Promise<void>
  // If provided, the modal shows a "View work details" button that closes the
  // modal and invokes this. AppDetail wires it to focus the work area; pages
  // without a work area should omit it.
  onViewWorkDetails?: (app: App) => void
}

export function usePublishApp(options: UsePublishAppOptions = {}) {
  const { onPublishStarted, onViewWorkDetails } = options
  const [targetApp, setTargetApp] = useState<App | null>(null)
  const [show, setShow] = useState(false)
  const [preflight, setPreflight] = useState<SourcePreflightResponse | null>(null)
  const [preflightError, setPreflightError] = useState<string | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [accessLevel, setAccessLevel] = useState('platform')
  const [submitting, setSubmitting] = useState(false)

  const close = useCallback(() => {
    setShow(false)
    setTargetApp(null)
    setPreflight(null)
    setPreflightError(null)
    setSubmitError(null)
  }, [])

  const startPublish = useCallback(async (app: App) => {
    setTargetApp(app)
    setSubmitError(null)
    setSubmitting(true)
    try {
      const response = await preflightMakeAppLive(app.name)
      setPreflight(response)
      setPreflightError(null)
    } catch (err) {
      setPreflight(null)
      setPreflightError(err instanceof Error ? err.message : 'The safety check could not run.')
    } finally {
      setSubmitting(false)
    }
    setAccessLevel('platform')
    setShow(true)
  }, [])

  const confirm = useCallback(async () => {
    if (!targetApp) return
    const app = targetApp
    setSubmitError(null)
    setSubmitting(true)
    try {
      if (accessLevel !== app.access_level) {
        try {
          await updateApp(app.name, { access_level: accessLevel } as Partial<App>)
        } catch (err) {
          console.error('Failed to update access level:', err)
        }
      }
      await triggerProductionBuild(app.name)
      // Success: close the modal and let the caller refresh.
      setShow(false)
      setTargetApp(null)
      setPreflight(null)
      setPreflightError(null)
      await onPublishStarted?.(app)
    } catch (err) {
      // Failure: keep the modal open so the user sees the error and can
      // retry from the same place. The submitError prop renders an inline
      // alert above the action buttons.
      const message = err instanceof Error ? err.message : 'Failed to start publishing.'
      console.error('Failed to start production build:', err)
      setSubmitError(message)
    } finally {
      setSubmitting(false)
    }
  }, [targetApp, accessLevel, onPublishStarted])

  const modal = useMemo(() => {
    if (!show || !targetApp) return null
    return (
      <PublishAppModal
        app={targetApp}
        preflight={preflight}
        preflightError={preflightError}
        submitError={submitError}
        accessLevel={accessLevel}
        onAccessLevelChange={setAccessLevel}
        onClose={close}
        onConfirm={confirm}
        submitting={submitting}
        onViewWorkDetails={onViewWorkDetails ? () => { close(); onViewWorkDetails(targetApp) } : undefined}
      />
    )
  }, [show, targetApp, preflight, preflightError, submitError, accessLevel, submitting, close, confirm, onViewWorkDetails])

  return { startPublish, modal, isPublishing: submitting }
}
